
import { userMapActions, userMapGetters } from "@/store/modules/user";
import { cloneDeep } from "lodash";
import Vue from "vue";
import UserSearchOptionComponent from "@/components/FormFields/SelectFieldOptions/UserSearchOptionComponent.vue";
import { capitalizeFirstLetter } from "@/helpers/generalHelpers";
import { userRoleArr } from "@/helpers/selectOptions";

interface IData {
  loading: boolean;
  options: { label: string; value: any }[];
}

export default Vue.extend({
  name: "search-select-users",
  props: {
    selected: {
      type: [Number, String, Array, Boolean],
      default: ""
    },
    value: {
      type: [Number, String, Array, Boolean],
      default: undefined
    },
    label: {
      type: String,
      default: "",
      required: false
    },
    selectedLabel: {
      type: String,
      required: false,
      default: ""
    },
    roles: {
      type: Array,
      required: false,
      default: () => userRoleArr
    },
    preFetch: {
      type: Boolean,
      required: false,
      default: false
    },
    multiple: {
      type: Boolean,
      required: false,
      default: false
    },
    placeholder: {
      type: String,
      required: false,
      default: "Type to search users"
    },
    returnWithout: {
      type: String,
      required: false,
      default: ""
    }
  },
  mounted() {
    if (this.preFetch) {
      this.remoteMethod("");
    }
  },
  watch: {
    effectiveSelected: {
      immediate: true,
      handler(current: any, previous: any) {
        const hasSelectedUsers =
          (typeof current === "string" && current.length >= 24) ||
          (Array.isArray(current) && current.length > 0);
        if (!hasSelectedUsers) return;
        const ids: string[] = typeof current === "string" ? [current] : current;
        const prevIds: string[] = Array.isArray(previous)
          ? previous
          : typeof previous === "string" && previous
          ? [previous]
          : [];
        const sameIds =
          ids.length === prevIds.length &&
          ids.every((id, i) => id === prevIds[i]);
        if (sameIds) return;
        this.makeOptionsForSelectedUsers(ids);
      }
    }
  },
  data(): IData {
    return {
      loading: false,
      options: []
    };
  },
  methods: {
    optionsComponent() {
      return UserSearchOptionComponent;
    },
    ...userMapActions(["getUsers"]),
    async remoteMethod(searchTerm: string) {
      try {
        this.loading = true;
        await this.getUsers({
          query: {
            __query: searchTerm
          },
          merge: true
        });
      } catch (error) {
        this.$appNotifyError("Error fetching users");
        this.$bugSnagClient.notify(error);
      } finally {
        this.loading = true;
      }
    },
    async makeOptionsForSelectedUsers(selected: string[]): Promise<void> {
      await this.getUsers({
        query: { _id__in: selected },
        merge: true
      });
    },
    makeUserOptions(users: any[]) {
      return users.map((user: any) => {
        const { _id, firstName, lastName } = user;
        let name = `${capitalizeFirstLetter(firstName)} ${capitalizeFirstLetter(
          lastName
        )}`;
        return {
          label: name,
          key: _id,
          value: _id,
          name,
          email: `${user.email}`,
          role: `${user.role}`
        };
      });
    }
  },
  computed: {
    ...userMapGetters(["getUsersFromStore"]),
    effectiveSelected(): any {
      return this.value !== undefined ? this.value : this.selected;
    },
    userListOptions(): any {
      const users = cloneDeep(this.getUsersFromStore);
      // filter users where role is not this.returnWithout
      let filteredUsers = users;
      if (this.returnWithout) {
        filteredUsers = users.filter((user: any) => {
          return user.role !== this.returnWithout;
        });
      }
      return this.makeUserOptions(filteredUsers);
    }
  }
});
